import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar, Clock, AlertCircle, MessageCircle, Phone, Mail,
  RefreshCw, BookOpen, ExternalLink, ArrowLeft, Users,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BUCKETS = [
  { key: "today",   label: "Expiring Today",        tone: "red",    hint: "0 days remaining" },
  { key: "3",       label: "Next 3 Days",           tone: "orange", hint: "3-day window" },
  { key: "7",       label: "Next 7 Days",           tone: "yellow", hint: "7-day window" },
  { key: "15",      label: "Next 15 Days",          tone: "yellow", hint: "15-day window" },
  { key: "30",      label: "Next 30 Days",          tone: "green",  hint: "30-day window" },
  { key: "expired", label: "Already Expired",       tone: "red",    hint: "Pending renewal" },
];

const MODULES = [
  { key: "all",    label: "All",    hint: "combined subscription" },
  { key: "hostel", label: "Hostel", hint: "residence paid-through" },
  { key: "mess",   label: "Mess",   hint: "mess paid-through" },
];

const TONE_STYLES = {
  red:    "bg-destructive/10 text-destructive border-destructive/30",
  orange: "bg-orange-100 text-orange-800 border-orange-200",
  yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  green:  "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const DOT = { red: "bg-destructive", orange: "bg-orange-500", yellow: "bg-yellow-500", green: "bg-emerald-500" };

export default function ExpiringResidents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const active = params.get("bucket") || "30";
  const activeModule = params.get("module") || "all";
  const [data, setData] = useState({ residents: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});

  // Renew dialog state
  const [renewFor, setRenewFor] = useState(null);
  const [renewForm, setRenewForm] = useState({
    new_end_date: "", months: 1, reason: "",
  });
  const [renewSaving, setRenewSaving] = useState(false);

  const canRenew = ["admin", "warden", "hostel_manager"].includes(user?.role || "");
  const canCall = user?.role !== "resident";

  const loadCounts = () => {
    api.get("/dashboard/stats").then((r) => setCounts(r.data?.expiring || {})).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams({ bucket: active, module: activeModule });
    api.get(`/dashboard/expiring?${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ residents: [], count: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadCounts(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [active, activeModule]);

  const setBucket = (key) => setParams({ bucket: key, module: activeModule });
  const setModule = (m) => setParams({ bucket: active, module: m });

  // Bucket counts respect current module filter (Hostel / Mess / All)
  const bucketCount = (k) => {
    const src = activeModule === "hostel" ? counts.hostel : activeModule === "mess" ? counts.mess : counts;
    if (!src) return "—";
    return src[k === "3" ? "next_3" : k === "7" ? "next_7" : k === "15" ? "next_15" : k === "30" ? "next_30" : k];
  };

  const openRenew = (resident) => {
    const base = resident.expiry_date || new Date().toISOString().slice(0, 10);
    const d = new Date(base); d.setMonth(d.getMonth() + 1);
    setRenewFor(resident);
    setRenewForm({
      new_end_date: d.toISOString().slice(0, 10),
      months: 1,
      reason: "",
    });
  };

  const changeMonths = (m) => {
    const base = renewFor?.expiry_date || new Date().toISOString().slice(0, 10);
    const d = new Date(base); d.setMonth(d.getMonth() + Number(m || 0));
    setRenewForm({ ...renewForm, months: Number(m), new_end_date: d.toISOString().slice(0, 10) });
  };

  const submitRenew = async (e) => {
    e.preventDefault();
    if (!renewForm.new_end_date) { toast.error("Pick a new end date"); return; }
    setRenewSaving(true);
    try {
      await api.post(`/residents/${renewFor.id}/renew`, renewForm);
      toast.success(`${renewFor.name} renewed until ${renewForm.new_end_date}`);
      setRenewFor(null);
      load(); loadCounts();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRenewSaving(false); }
  };

  const whatsappReminder = (r) => {
    const phone = (r.phone || "").replace(/\D/g, "");
    const bal = Number(r.outstanding || 0).toLocaleString("en-IN");
    const msg = `Dear ${r.name},

Your hostel subscription will expire on ${r.expiry_date}.
Please renew your hostel accommodation before the expiry date to avoid interruption of your stay.

Outstanding Balance: ₹${bal}

Thank you,
Param Social Foundation`;
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const sendSms = (r) => {
    const phone = r.phone || "";
    if (!phone) { toast.error("No mobile number on file"); return; }
    const msg = `Dear ${r.name}, your hostel subscription expires on ${r.expiry_date}. Balance ₹${Number(r.outstanding || 0).toLocaleString("en-IN")}. — Param Social Foundation`;
    // sms: URI opens default SMS app on mobile / desktop shortcut on macOS
    window.location.href = `sms:${phone}?body=${encodeURIComponent(msg)}`;
  };

  const sendEmail = (r) => {
    if (!r.email) { toast.error("No email on file"); return; }
    const subject = "Hostel Subscription Renewal Reminder";
    const body = `Dear ${r.name},

Your hostel subscription will expire on ${r.expiry_date}.
Please renew before the expiry date to avoid interruption.

Outstanding Balance: ₹${Number(r.outstanding || 0).toLocaleString("en-IN")}

Regards,
Param Social Foundation`;
    window.location.href = `mailto:${r.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const callResident = (r) => {
    const phone = r.phone || "";
    if (!phone) { toast.error("No mobile number on file"); return; }
    window.location.href = `tel:${phone}`;
  };

  return (
    <div className="space-y-6" data-testid="expiring-residents-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Renewals</div>
          <h1 className="font-display text-4xl font-bold mt-1">Subscriptions Expiring Soon</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Every active resident whose stay or monthly subscription is close to expiring.
            Click a bucket to drill in, then use quick actions to remind, renew or collect balances.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
        </Button>
      </div>

      {/* MODULE TABS */}
      <div className="flex gap-2" data-testid="module-tabs">
        {MODULES.map((m) => (
          <button
            key={m.key}
            onClick={() => setModule(m.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-all ${
              activeModule === m.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-white border-border hover:border-primary/40"
            }`}
            data-testid={`module-${m.key}`}
          >
            {m.label}
            <span className="ml-2 text-xs opacity-70">· {m.hint}</span>
          </button>
        ))}
      </div>

      {/* BUCKET CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {BUCKETS.map((b) => {
          const value = bucketCount(b.key);
          const isActive = active === b.key;
          return (
            <button
              key={b.key}
              onClick={() => setBucket(b.key)}
              className={`text-left p-4 rounded-md border transition-all ${
                isActive ? "border-primary shadow-md bg-white" : "border-border bg-white hover:shadow-sm"
              }`}
              data-testid={`bucket-${b.key}`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${DOT[b.tone]}`} />
                <span className="eyebrow text-muted-foreground">{b.label}</span>
              </div>
              <div className="font-display text-3xl font-bold mt-2">{value ?? "—"}</div>
              {activeModule === "all" ? (
                <div className="flex items-center gap-2 text-[11px] mt-1">
                  <span className="text-muted-foreground">Hostel</span>
                  <span className="font-semibold">{counts.hostel?.[b.key === "3" ? "next_3" : b.key === "7" ? "next_7" : b.key === "15" ? "next_15" : b.key === "30" ? "next_30" : b.key] ?? 0}</span>
                  <span className="text-muted-foreground">· Mess</span>
                  <span className="font-semibold">{counts.mess?.[b.key === "3" ? "next_3" : b.key === "7" ? "next_7" : b.key === "15" ? "next_15" : b.key === "30" ? "next_30" : b.key] ?? 0}</span>
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground mt-1">{b.hint}</div>
              )}
            </button>
          );
        })}
      </div>

      {/* SUMMARY STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Renewed this month" value={counts.renewed_this_month ?? "—"} tone="green" />
        <MiniStat label="Pending renewals" value={counts.expired ?? "—"} tone="red" />
        <MiniStat label="Expiring in 7 days" value={counts.next_7 ?? "—"} tone="yellow" />
        <MiniStat label={active === "expired" ? "Expired outstanding"
                       : active === "today" ? "Today outstanding"
                       : `Next ${active}d outstanding`}
          value={`₹${Number((counts.outstanding_by_bucket?.[active === "3" ? "next_3" : active === "7" ? "next_7" : active === "15" ? "next_15" : active === "30" ? "next_30" : active] ?? counts.outstanding_total) || 0).toLocaleString("en-IN")}`}
          tone="orange" />
      </div>

      {/* TABLE */}
      <Card className="p-4 border-border bg-white">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="eyebrow text-muted-foreground">Drill-down</div>
            <div className="font-display text-2xl font-bold">
              {BUCKETS.find((x) => x.key === active)?.label}
              <span className="ml-3 text-muted-foreground text-lg">({data.count})</span>
            </div>
          </div>
        </div>
        {loading && <div className="py-6 text-center text-muted-foreground">Loading…</div>}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expiring-table">
              <thead>
                <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                  <th className="py-3 pr-3"></th>
                  <th className="py-3 pr-3">Name</th>
                  <th className="py-3 pr-3">Mobile</th>
                  <th className="py-3 pr-3">Institute</th>
                  <th className="py-3 pr-3">Room</th>
                  <th className="py-3 pr-3">Check-in</th>
                  <th className="py-3 pr-3">Expiry</th>
                  <th className="py-3 pr-3 text-right">Days</th>
                  <th className="py-3 pr-3">Residence Fee</th>
                  <th className="py-3 pr-3">Mess Fee</th>
                  <th className="py-3 pr-3 text-right">Outstanding</th>
                  <th className="py-3 pr-3">Renewal</th>
                  <th className="py-3 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.residents.map((r) => (
                  <tr key={r.id} className="border-b border-border/40 hover:bg-secondary/40"
                      data-testid={`expiring-row-${r.id}`}>
                    <td className="py-2 pr-3">
                      {r.photo ? (
                        <img src={r.photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-[11px] font-bold text-muted-foreground">
                          {(r.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 font-medium">{r.name}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.phone || "—"}</td>
                    <td className="py-2 pr-3 text-muted-foreground truncate max-w-[140px]">{r.company || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {r.building || "—"}{r.floor ? `/${r.floor}` : ""}{r.room_no ? `/${r.room_no}` : ""}{r.bed_no ? `-${r.bed_no}` : ""}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.joining_date || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      <div>{r.expiry_date}</div>
                      {activeModule === "all" && (r.hostel_expiry !== r.mess_expiry) && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                          H: {r.hostel_expiry || "—"}<br/>
                          M: {r.mess_expiry || "—"}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${TONE_STYLES[r.color]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${DOT[r.color]}`} />
                        {r.days_remaining < 0 ? `${Math.abs(r.days_remaining)} overdue` : `${r.days_remaining} days`}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant={r.residence_fee_status === "clear" ? "outline" : "destructive"} className="capitalize text-[10px]">
                        {r.residence_fee_status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant={r.mess_fee_status === "clear" ? "outline" : "destructive"} className="capitalize text-[10px]">
                        {r.mess_fee_status.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {r.outstanding > 0 ? (
                        <span className="text-destructive font-semibold">₹{r.outstanding.toLocaleString("en-IN")}</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-3">
                      {r.renewal_status === "renewed_this_month" ? (
                        <Badge className="text-[10px]">Renewed</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">Pending</Badge>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <IconBtn tid={`view-${r.id}`} title="View profile"
                          onClick={() => navigate(`/residents?highlight=${r.id}`)}><ExternalLink className="w-3 h-3" /></IconBtn>
                        {canRenew && (
                          <IconBtn tid={`renew-${r.id}`} title="Renew stay" tone="primary"
                            onClick={() => openRenew(r)}><RefreshCw className="w-3 h-3" /></IconBtn>
                        )}
                        <IconBtn tid={`ledger-${r.id}`} title="Ledger"
                          onClick={() => navigate(`/residents/${r.id}/ledger`)}><BookOpen className="w-3 h-3" /></IconBtn>
                        <IconBtn tid={`wa-${r.id}`} title="WhatsApp reminder" tone="green"
                          onClick={() => whatsappReminder(r)}><MessageCircle className="w-3 h-3" /></IconBtn>
                        {r.phone && (
                          <IconBtn tid={`sms-${r.id}`} title="SMS reminder"
                            onClick={() => sendSms(r)}><Mail className="w-3 h-3 rotate-90" /></IconBtn>
                        )}
                        {r.email && (
                          <IconBtn tid={`email-${r.id}`} title="Email reminder"
                            onClick={() => sendEmail(r)}><Mail className="w-3 h-3" /></IconBtn>
                        )}
                        {canCall && r.phone && (
                          <IconBtn tid={`call-${r.id}`} title="Call resident"
                            onClick={() => callResident(r)}><Phone className="w-3 h-3" /></IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!data.residents.length && (
                  <tr><td colSpan={14} className="py-10 text-center text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="w-8 h-8 opacity-40" />
                      No residents in this bucket. Nice!
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* RENEW DIALOG */}
      <Dialog open={!!renewFor} onOpenChange={(v) => !v && setRenewFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Renew Stay — {renewFor?.name}</DialogTitle>
          </DialogHeader>
          {renewFor && (
            <form onSubmit={submitRenew} className="space-y-4">
              <div className="text-sm text-muted-foreground border border-border rounded-md p-3 bg-secondary/30">
                Current expiry: <span className="font-mono">{renewFor.expiry_date}</span>
                {" • "}Outstanding: <span className="font-semibold text-foreground">₹{Number(renewFor.outstanding || 0).toLocaleString("en-IN")}</span>
              </div>
              <div className="space-y-2">
                <Label>Extend by (months)</Label>
                <div className="flex gap-2">
                  {[1, 3, 6, 12].map((m) => (
                    <Button
                      key={m} type="button" size="sm"
                      variant={renewForm.months === m ? "default" : "outline"}
                      onClick={() => changeMonths(m)}
                      data-testid={`renew-months-${m}`}
                    >
                      {m} mo
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>New End Date *</Label>
                <Input required type="date" value={renewForm.new_end_date}
                  onChange={(e) => setRenewForm({ ...renewForm, new_end_date: e.target.value, months: 0 })}
                  data-testid="renew-end-date" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={renewForm.reason}
                  onChange={(e) => setRenewForm({ ...renewForm, reason: e.target.value })}
                  placeholder="Payment reference / receipt no. / manager note..."
                  data-testid="renew-reason" />
              </div>
              <p className="text-xs text-muted-foreground">
                This updates the resident's expiry date and appends to the renewal history.
                It does <span className="font-semibold">not</span> create a new invoice — collect fees separately from the fees page.
              </p>
              <DialogFooter>
                <Button type="submit" disabled={renewSaving} data-testid="renew-submit">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {renewSaving ? "Saving..." : "Confirm Renewal"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const IconBtn = ({ tid, title, tone, children, onClick }) => (
  <Button
    size="sm"
    variant="outline"
    className={`h-7 w-7 p-0 ${
      tone === "green" ? "text-green-700 hover:text-green-800" :
      tone === "primary" ? "text-primary hover:text-primary" : ""
    }`}
    onClick={onClick}
    title={title}
    data-testid={tid}
  >
    {children}
  </Button>
);

const MiniStat = ({ label, value, tone }) => (
  <Card className="p-3 border-border bg-white flex items-center gap-3">
    <span className={`w-2 h-8 rounded-full ${DOT[tone]}`} />
    <div>
      <div className="eyebrow text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-xl mt-1">{value}</div>
    </div>
  </Card>
);
